import { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { studentDueFeeTypeWise } from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { Link } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import { feeTypes } from "../config/Data";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";
import { getBatchList } from "../../api/BatchService";
import { busList } from "../../api/Transport";

const FeeTypes = [...feeTypes, "Library Fee", "Transport Fee", "Hostel Fee"];

const FEE_TYPE_CONFIG = {
  "Hostel Fee": {
    relationKey: "student_hostel_n_fees",
    getDue: (r) => Number(r.left_amount || 0),
    getTotal: (r) => Number(r.final_amount || 0),
    getPaid: (r) => Number(r.paid_amount || 0),
    extra: (r) => r.month,
  },
  "Library Fee": {
    relationKey: "student_library_n_fees",
    getDue: (r) => Number(r.left_amount || 0),
    getTotal: (r) => Number(r.final_amount || 0),
    getPaid: (r) => Number(r.paid_amount || 0),
    extra: (r) => r.month,
  },
  "Transport Fee": {
    relationKey: "student_transport_n_fees",
    getDue: (r) => Number(r.final_fee || 0) - Number(r.paid_fee || 0),
    getTotal: (r) => Number(r.final_fee || 0),
    getPaid: (r) => Number(r.paid_fee || 0),
    extra: () => null,
  },
  DEFAULT: {
    relationKey: "student_batch_fees_details",
    getDue: (r) => Number(r.due_amount || 0),
    getTotal: (r) => Number(r.final_amount || 0),
    getPaid: (r) => Number(r.paid_amount || 0),
    extra: () => null,
  },
};

const getFeeConfig = (feeType) =>
  FEE_TYPE_CONFIG[feeType] ?? FEE_TYPE_CONFIG.DEFAULT;

export default function StudentFeeDueTypeWise() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [buses, setBuses] = useState([]);

  const [filters, setFilters] = useState({
    fee_type: FeeTypes?.[0],
    batch_id: "",
    bus_id: "",
  });

  const [students, setStudents] = useState([]);

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const getBatchListData = async () => {
    try {
      const addUrl = filters.session_id
        ? `&session_id=${filters.session_id}&limit=all`
        : "limit=all";

      const response = await getBatchList(addUrl);

      if (response?.data?.status) {
        setBatches(response?.data?.data || response?.data?.batches || []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Batch list error:", error);
      setBatches([]);
    }
  };

  const getBusList = async () => {
    try {
      const response = await busList("limit=all");

      if (response?.data?.status) {
        setBuses(response?.data?.data || []);
      } else {
        setBuses([]);
      }
    } catch (error) {
      console.error("Bus list error:", error);
      setBuses([]);
    }
  };

  useEffect(() => {
    if (!filters.session_id) return;

    getBatchListData();
  }, [filters.session_id]);

  useEffect(() => {
    getBusList();
  }, []);

  const inr = (n) =>
    "₹ " +
    Number(n || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });

  const config = useMemo(
    () => getFeeConfig(filters.fee_type),
    [filters.fee_type],
  );

  const rows = useMemo(() => {
    return (students || [])
      .map((student) => {
        const records = student?.[config.relationKey] || [];
        const dueRecords = records.filter((r) => config.getDue(r) > 0);
        const totalDue = dueRecords.reduce(
          (sum, r) => sum + config.getDue(r),
          0,
        );

        return {
          id: student.id,
          registration_no: student.registration_no,
          name: student.name,
          father_name: student.father_name,
          phone: student.phone,
          due_amount: totalDue,
          records: dueRecords,
        };
      })
      .filter((s) => s.due_amount > 0);
  }, [students, config]);

  const totalDueAmount = useMemo(
    () => rows.reduce((sum, r) => sum + r.due_amount, 0),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const search = filters.search?.trim().toLowerCase();
    if (!search) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(search) ||
        r.registration_no?.toLowerCase().includes(search) ||
        r.phone?.toLowerCase().includes(search),
    );
  }, [rows, filters.search]);

  const getStudentDueFeeTypeWise = async () => {
    if (!filters?.fee_type) return;

    setLoading(true);
    try {
      let addUrl = `fee_type=${encodeURIComponent(filters.fee_type)}`;
      Object.keys(filters).forEach((key) => {
        if (key === "fee_type" || key === "search") return;
        const value = filters[key];
        if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
      });

      const response = await studentDueFeeTypeWise(addUrl);
      if (response?.data?.status) {
        setStudents(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filters?.fee_type || !filters?.session_id) return;
    getStudentDueFeeTypeWise();
  }, [filters.fee_type, filters.session_id, filters.batch_id, filters.bus_id]);

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      fee_type: FeeTypes?.[0] || "",
      batch_id: "",
      bus_id: "",
      search: "",
    }));
  };

  const handleDownload = async (type = "excel") => {
    setLoading(true);
    try {
      const downloadData = [
        ...filteredRows.map((student, index) => ({
          "Registration No": student.registration_no,
          "Student Name": student.name,
          "Father Name": student.father_name,
          Phone: student.phone || "",
          Records: student.records.length,
          "Due Amount": student.due_amount,
        })),

        {
          "Registration No": "",
          "Student Name": "TOTAL",
          "Father Name": "",
          Phone: "",
          Records: filteredRows.reduce(
            (total, student) => total + student.records.length,
            0,
          ),
          "Due Amount": totalDueAmount,
        },
      ];
      if (downloadData.length) {
        if (type === "excel") {
          const now = new Date();

          const fileName = `Report_${now.getFullYear()}_${now.getMonth() + 1
            }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    {/* Header / Filters */}
                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-danger text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{ width: 46, height: 46 }}
                          >
                            <i className="ri-hand-coin-line"></i>
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">
                              Student Due Fee — Type Wise
                            </h5>
                            <small className="text-muted">
                              View outstanding dues for a single fee type across
                              all students
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="row align-items-center g-3">
                        <div className="col-md-3">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select rounded-3"
                              value={filters?.fee_type || ""}
                              onChange={(e) => {
                                const feeType = e.target.value;
                                setFilters((prev) => ({
                                  ...prev,
                                  fee_type: feeType,
                                  bus_id:
                                    feeType === "Transport Fee"
                                      ? prev.bus_id
                                      : "",
                                }));
                              }}
                            >
                              {FeeTypes?.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                            <label>Fee Type</label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select rounded-3"
                              value={filters?.batch_id || ""}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  batch_id: e.target.value,
                                }))
                              }
                            >
                              <option value="">All Batches</option>
                              {batches?.map((batch) => (
                                <option key={batch.id} value={batch.id}>
                                  {batch.title}
                                </option>
                              ))}
                            </select>
                            <label>Batch</label>
                          </div>
                        </div>
                        {filters?.fee_type === "Transport Fee" && (
                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.bus_id || ""}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    bus_id: e.target.value,
                                  }))
                                }
                              >
                                <option value="">All Buses</option>
                                {buses?.map((bus) => (
                                  <option key={bus.id} value={bus.id}>
                                    {bus.title ||
                                      bus.name ||
                                      bus.bus_number ||
                                      `Bus #${bus.id}`}
                                  </option>
                                ))}
                              </select>
                              <label>Bus</label>
                            </div>
                          </div>
                        )}
                        <div
                          className={
                            filters?.fee_type === "Transport Fee"
                              ? "col-md-3"
                              : "col-md-3"
                          }
                        >
                          <div className="form-floating form-floating-outline">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Name / Reg. No / Phone"
                              value={filters?.search || ""}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  search: e.target.value,
                                }))
                              }
                            />
                            <label>Name / Reg. No / Phone</label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select rounded-3"
                              value={filters?.session_id || ""}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  session_id: e.target.value,
                                  batch_id: "",
                                }))
                              }
                            >
                              {sessions?.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.session} :{" "}
                                  {Number(s.status) === 1
                                    ? "Active"
                                    : "Inactive"}
                                </option>
                              ))}
                            </select>
                            <label>Session</label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-3 w-100"
                            onClick={handleClearFilters}
                          >
                            <i className="ri-filter-off-line me-2"></i>
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ============ Summary Cards ============ */}
                    <div className="row g-3 mb-3">
                      {[
                        {
                          label: "Students",
                          value: filteredRows.length,
                          icon: "ri-group-line",
                          accent: "#4f46e5",
                          isCurrency: false,
                        },
                        {
                          label: "Due Entries",
                          value: filteredRows.reduce(
                            (sum, r) => sum + r.records.length,
                            0,
                          ),
                          icon: "ri-file-list-3-line",
                          accent: "#0ea5e9",
                          isCurrency: false,
                        },
                        {
                          label: "Total Due",
                          value: filteredRows.reduce(
                            (sum, r) => sum + r.due_amount,
                            0,
                          ),
                          icon: "ri-error-warning-line",
                          accent: "#ef4444",
                          isCurrency: true,
                        },
                      ].map((k) => (
                        <div className="col" key={k.label}>
                          <div
                            className="card border-0 shadow-sm rounded-4 h-100 kpi-card"
                            style={{ borderTop: `3px solid ${k.accent}` }}
                          >
                            <div className="card-body p-3">
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center mb-2 fs-6"
                                style={{
                                  width: 42,
                                  height: 42,
                                  background: `${k.accent}1a`,
                                  color: k.accent,
                                }}
                              >
                                <i className={k.icon}></i>
                              </div>
                              <h5
                                className="m-0 fw-bold"
                                style={{ color: k.accent }}
                              >
                                {k.isCurrency ? inr(k.value) : k.value}
                              </h5>
                              <small className="text-muted text-uppercase fw-semibold">
                                {k.label}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ============ Report Table ============ */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #ef4444, #f59e0b)",
                            }}
                          >
                            <i className="ri-file-chart-line fs-5"></i>
                          </div>
                          <div>
                            <h6 className="m-0 fw-bold">
                              Students Due — {filters?.fee_type}
                            </h6>
                            <small className="text-muted">
                              One row per student, dues rolled up per fee type
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                            <i className="ri-list-check me-1"></i>
                            {filteredRows.length} student
                            {filteredRows.length === 1 ? "" : "s"}
                          </span>
                          {hasAccess("/fees/excel/list") && (
                            <button
                              type="button"
                              className="btn btn-outline-primary rounded-3"
                              onClick={() => handleDownload("excel")}
                            >
                              <i className="ri-file-excel-2-line me-2"></i>{" "}
                              Download
                            </button>
                          )}

                          {hasAccess("/fees/print/list") && (
                            <button
                              type="button"
                              className="btn btn-outline-info rounded-3"
                              onClick={() => handleDownload("print")}
                            >
                              <i className="ri-printer-line me-2"></i> Print
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4 py-3 fw-semibold">#</th>
                              <th className="py-3 fw-semibold">Student</th>
                              <th className="py-3 fw-semibold">Phone</th>
                              <th className="py-3 fw-semibold text-end">
                                Records
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                Due Amount
                              </th>
                              <th className="py-3 fw-semibold pe-4 text-end">
                                Action
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredRows.length > 0 ? (
                              filteredRows.map((student, index) => (
                                <tr key={student.id}>
                                  <td className="ps-4">{index + 1}</td>
                                  <td>
                                    <Link
                                      className="fw-semibold text-decoration-none"
                                      to={`/student/details?id=${student.id}`}
                                    >
                                      {student.name}{" "}
                                      <span className="text-muted fw-normal">
                                        ({student.registration_no})
                                      </span>
                                    </Link>
                                    <div className="text-muted small">
                                      {student.father_name}
                                    </div>
                                  </td>
                                  <td>{student.phone || "-"}</td>
                                  <td className="text-end">
                                    <span className="badge bg-label-secondary">
                                      {student.records.length}
                                    </span>
                                  </td>
                                  <td className="text-end text-nowrap fw-bold text-danger">
                                    {inr(student.due_amount)}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2 align-items-center justify-content-end">
                                      {hasAccess("/student/details") && (
                                        <Link
                                          className="btn btn-label-primary btn-sm rounded-3"
                                          to={`/student/details?id=${student.id}`}
                                          title="View Student"
                                          aria-label="View Student"
                                        >
                                          <i className="ri-eye-line"></i>
                                        </Link>
                                      )}
                                      {hasAccess("/student/fees/deposit") && (
                                        <Link
                                          className="btn btn-label-danger btn-sm rounded-3"
                                          to={`/student/fees/deposit?student_id=${student.id}`}
                                          title="Collect Fee"
                                          aria-label="Collect Fee"
                                        >
                                          <i className="ri-wallet-3-line"></i>
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-inbox-line fs-2 d-block mb-2 text-secondary"></i>
                                  No Due Fee Records Found for{" "}
                                  {filters?.fee_type}
                                </td>
                              </tr>
                            )}
                          </tbody>

                          {filteredRows.length > 0 && (
                            <tfoot>
                              <tr style={{ background: "#f8f9fc" }}>
                                <td
                                  colSpan="4"
                                  className="ps-4 py-3 text-end fw-bold"
                                >
                                  Page Total
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap text-danger">
                                  {inr(totalDueAmount)}
                                </td>
                                <td className="pe-4"></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
